import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Plus, Check, Archive } from "lucide-react";
import Header from "@/components/Header";
import LoadingState from "@/components/LoadingState";
import PullToRefreshIndicator from "@/components/PullToRefreshIndicator";
import { Grievance } from "@/data/grievances";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { useCases, useMyCases, useCaseImages } from "@/services/queries";
import { API_BASE_URL } from "@/services/api";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { addLog } from "@/logger";
import GrievanceCard from "@/components/GrievanceCard";
import { usePullToRefresh } from "@/hooks/use-pull-to-refresh";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { getStoredPhoneId } from "@/utils/deviceId";

const Index = () => {
  const navigate = useNavigate();
  const authState = useSelector((state: RootState) => state.auth);
  const [searchQuery, setSearchQuery] = useState("");
  const [isResolvedOpen, setIsResolvedOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"all" | "my">("all");
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const hasLoggedAuth = useRef(false);

  // Only fetch cases when auth is ready and we have a token (avoid requesting without auth)
  const shouldFetchCases = Boolean(authState.authReady && authState.token);

  // Get device ID for "My Cases" filtering
  useEffect(() => {
    if (shouldFetchCases) {
      getStoredPhoneId()
        .then((id) => {
          setDeviceId(id);
          addLog(`Device ID loaded: ${id}`);
        })
        .catch((err) => {
          addLog(`Failed to get device ID: ${err}`);
        });
    }
  }, [shouldFetchCases]);

  // Log auth state only once to prevent infinite loop
  useEffect(() => {
    if (!hasLoggedAuth.current) {
      addLog(
        `Auth state: token exists=${!!authState.token}, authReady=${
          authState.authReady
        }`
      );
      hasLoggedAuth.current = true;
    }
  }, [authState.token, authState.authReady]);

  const { data: allCasesData, isLoading: isLoadingAll, isError: isErrorAll, refetch: refetchAll } = useCases(
    shouldFetchCases && activeTab === "all"
  );
  const { data: myCasesData, isLoading: isLoadingMy, isError: isErrorMy, refetch: refetchMy } = useMyCases(
    deviceId,
    shouldFetchCases && activeTab === "my" && deviceId !== null
  );

  // Use appropriate data based on active tab
  const grievances = activeTab === "all" ? (allCasesData ?? []) : (myCasesData ?? []);
  const isLoading = activeTab === "all" ? isLoadingAll : isLoadingMy;
  const isError = activeTab === "all" ? isErrorAll : isErrorMy;
  const refetch = activeTab === "all" ? refetchAll : refetchMy;

  const onRefresh = useCallback(async () => {
    if (shouldFetchCases) {
      await refetch();
    }
  }, [shouldFetchCases, refetch]);

  // Pull to refresh functionality
  const pullToRefresh = usePullToRefresh({
    onRefresh,
    enabled: shouldFetchCases,
  });

  const [filteredGrievances, setFilteredGrievances] = useState<Grievance[]>([]);
  const [resolvedGrievances, setResolvedGrievances] = useState<Grievance[]>([]);
  const processingRef = useRef(false);

  // Resolve first image IDs to URLs for list cards (so photos display on list view)
  const firstImageIds = [
    ...filteredGrievances,
    ...resolvedGrievances,
  ]
    .map((g) => g.images?.[0])
    .filter((id): id is string => Boolean(id));
  const uniqueFirstIds = [...new Set(firstImageIds)];
  const { data: firstImages = [] } = useCaseImages(
    uniqueFirstIds.map((id) => ({ id })),
    uniqueFirstIds.length > 0 && shouldFetchCases
  );
  const imageIdToUrl = new Map(firstImages.map((img) => [img.id, img.url]));

  // Helper: sort by created timestamp descending (newest first; same-day order by time)
  const sortByDate = useCallback((list: Grievance[]) => {
    return [...list].sort((a, b) => {
      const timeA = new Date(a.createdAt || a.date || 0).getTime();
      const timeB = new Date(b.createdAt || b.date || 0).getTime();
      return timeB - timeA;
    });
  }, []);

  // Process grievances when API data is available (depend on grievances, not defaulted array, to avoid infinite loop)
  useEffect(() => {
    if (processingRef.current) return;
    processingRef.current = true;

    const list = grievances ?? [];
    if (list.length > 0) {
      addLog(`Processing ${list.length} API grievances`);

      // Active statuses: "All Cases" excludes pending (inspectors only show vetted/investigating cases);
      // "My Cases" includes pending so submitters see their newly submitted cases.
      const activeStatuses =
        activeTab === "all"
          ? ["in-progress", "open", "investigating"]
          : ["pending", "in-progress", "open", "investigating"];
      const activeGrievances = list.filter((g) =>
        g.status != null ? activeStatuses.includes(g.status) : false
      );

      const resolvedCases = list.filter(
        (g) =>
          g.status === "done" ||
          g.status === "closed" ||
          g.status === "resolved"
      );

      // Apply search filter if needed
      if (searchQuery === "") {
        setFilteredGrievances(sortByDate(activeGrievances));
        setResolvedGrievances(sortByDate(resolvedCases));
      } else {
        const searchLower = searchQuery.toLowerCase();

        const filteredActive = activeGrievances.filter(
          (g) =>
            g.title?.toLowerCase().includes(searchLower) ||
            false ||
            g.description?.toLowerCase().includes(searchLower) ||
            false ||
            g.location?.toLowerCase().includes(searchLower) ||
            false ||
            g.companyInvolved?.toLowerCase().includes(searchLower) ||
            false
        );

        const filteredResolved = resolvedCases.filter(
          (g) =>
            g.title?.toLowerCase().includes(searchLower) ||
            false ||
            g.description?.toLowerCase().includes(searchLower) ||
            false ||
            g.location?.toLowerCase().includes(searchLower) ||
            false ||
            g.companyInvolved?.toLowerCase().includes(searchLower) ||
            false
        );

        setFilteredGrievances(sortByDate(filteredActive));
        setResolvedGrievances(sortByDate(filteredResolved));
      }
    } else {
      // Reset the filtered lists when no data is available
      setFilteredGrievances([]);
      setResolvedGrievances([]);

      // Only log when we actually attempted to fetch (query was enabled). When query is disabled, isLoading is false and we must not treat that as "no data".
      if (shouldFetchCases) {
        if (isError) {
          addLog("Error loading grievances from API");
        } else if (!isLoading) {
          addLog(
            `No grievances available from API (backend: ${API_BASE_URL}). Check debug log and Network tab for response.`
          );
        }
      }
    }

    processingRef.current = false;
  }, [grievances, activeTab, searchQuery, isError, isLoading, shouldFetchCases, sortByDate]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  // Update loading state to reflect authentication readiness too
  const isLoadingData = isLoading || !authState.authReady;

  return (
    <div
      ref={pullToRefresh.containerRef}
      className="flex flex-col min-h-[100dvh] bg-background overflow-auto"
    >
      <PullToRefreshIndicator
        isVisible={pullToRefresh.shouldShowIndicator}
        isRefreshing={pullToRefresh.isRefreshing}
        pullDistance={pullToRefresh.pullDistance}
      />

      <Header title="Mining Matters" />

      <main className="flex-1 pt-24 pb-24 px-4 max-w-lg mx-auto w-full">
        <div className="relative mb-6 mt-4">
          <Input
            type="text"
            placeholder="Search grievances..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10"
          />
          <Search className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
        </div>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "all" | "my")} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="all">All Cases</TabsTrigger>
            <TabsTrigger value="my">My Cases</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-0">
            {isLoadingData ? (
              <LoadingState message="Loading grievance cases..." />
            ) : isError ? (
              <div className="text-center py-8 border border-dashed rounded-lg">
                <p className="text-muted-foreground">
                  Error loading cases. Please try again.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  className="mt-3"
                >
                  Retry
                </Button>
              </div>
            ) : (
              <ErrorBoundary>
              <div className="space-y-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold">Active Cases</h2>
                    <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                      {filteredGrievances.length}
                    </span>
                  </div>

              {filteredGrievances.length > 0 ? (
                <div className="space-y-3">
                  {filteredGrievances.map((grievance, index) => (
                    <GrievanceCard
                      key={grievance.id ?? index}
                      grievance={grievance}
                      index={index}
                      firstImageUrl={grievance.images?.[0] ? imageIdToUrl.get(grievance.images[0]) : undefined}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 border border-dashed rounded-lg">
                  <p className="text-muted-foreground">
                    {grievances.length === 0
                      ? "No grievances from API. Backend: " +
                        API_BASE_URL.replace(/\/api\/?$/, "") +
                        " — check Debug log and Network tab for response."
                      : "No active cases found"}
                  </p>
                </div>
              )}
            </div>

            {resolvedGrievances.length > 0 && (
              <Collapsible
                open={isResolvedOpen}
                onOpenChange={setIsResolvedOpen}
                className="space-y-3"
              >
                <CollapsibleTrigger className="flex items-center gap-2 w-full text-left">
                  <div className="flex items-center gap-2 py-1">
                    <Archive className="h-4 w-4 text-muted-foreground" />
                    <h2 className="text-lg font-semibold">Resolved Cases</h2>
                    <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                      {resolvedGrievances.length}
                    </span>
                  </div>
                  <div className="flex-grow" />
                  <Check
                    className={cn(
                      "h-4 w-4 transition-transform",
                      isResolvedOpen ? "rotate-180" : ""
                    )}
                  />
                </CollapsibleTrigger>

                <CollapsibleContent className="space-y-3">
                  {resolvedGrievances.map((grievance, index) => (
                    <GrievanceCard
                      key={grievance.id}
                      grievance={grievance}
                      index={index + filteredGrievances.length}
                      firstImageUrl={grievance.images?.[0] ? imageIdToUrl.get(grievance.images[0]) : undefined}
                    />
                  ))}
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
          </ErrorBoundary>
            )}
          </TabsContent>

          <TabsContent value="my" className="mt-0">
            {isLoadingData ? (
              <LoadingState message="Loading your cases..." />
            ) : isError ? (
              <div className="text-center py-8 border border-dashed rounded-lg">
                <p className="text-muted-foreground">
                  Error loading your cases. Please try again.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  className="mt-3"
                >
                  Retry
                </Button>
              </div>
            ) : !deviceId ? (
              <div className="text-center py-8 border border-dashed rounded-lg">
                <p className="text-muted-foreground">
                  Loading device information...
                </p>
              </div>
            ) : (
              <ErrorBoundary>
              <div className="space-y-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold">My Active Cases</h2>
                    <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                      {filteredGrievances.length}
                    </span>
                  </div>

                  {filteredGrievances.length > 0 ? (
                    <div className="space-y-3">
                      {filteredGrievances.map((grievance, index) => (
                        <GrievanceCard
                          key={grievance.id ?? index}
                          grievance={grievance}
                          index={index}
                          firstImageUrl={grievance.images?.[0] ? imageIdToUrl.get(grievance.images[0]) : undefined}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 border border-dashed rounded-lg">
                      <p className="text-muted-foreground">
                        {grievances.length === 0
                          ? "You haven't submitted any cases yet."
                          : "No active cases found"}
                      </p>
                    </div>
                  )}
                </div>

                {resolvedGrievances.length > 0 && (
                  <Collapsible
                    open={isResolvedOpen}
                    onOpenChange={setIsResolvedOpen}
                    className="space-y-3"
                  >
                    <CollapsibleTrigger className="flex items-center gap-2 w-full text-left">
                      <div className="flex items-center gap-2 py-1">
                        <Archive className="h-4 w-4 text-muted-foreground" />
                        <h2 className="text-lg font-semibold">My Resolved Cases</h2>
                        <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                          {resolvedGrievances.length}
                        </span>
                      </div>
                      <div className="flex-grow" />
                      <Check
                        className={cn(
                          "h-4 w-4 transition-transform",
                          isResolvedOpen ? "rotate-180" : ""
                        )}
                      />
                    </CollapsibleTrigger>

                    <CollapsibleContent className="space-y-3">
                      {resolvedGrievances.map((grievance, index) => (
                        <GrievanceCard
                          key={grievance.id}
                          grievance={grievance}
                          index={index + filteredGrievances.length}
                          firstImageUrl={grievance.images?.[0] ? imageIdToUrl.get(grievance.images[0]) : undefined}
                        />
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </div>
              </ErrorBoundary>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Operational disclosure — required by Play Store Misleading Claims policy */}
      <div className="pb-24 px-4 max-w-lg mx-auto w-full">
        <div className="mt-6 rounded-lg border border-border bg-muted/40 px-4 py-3 text-xs text-muted-foreground space-y-1">
          <p className="font-medium text-foreground/70">About This App</p>
          <p>
            Mining Matters is developed by Develop Digitally Limited on behalf of the Mines and Geology
            Division (MGD), Ministry of Agriculture, Fisheries and Mining. Grievances submitted through
            this app are reviewed and responded to by MGD.
          </p>
          <p>
            Official information: {" "}
            <a
              href="https://mgd.gov.jm"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-2"
            >
              mgd.gov.jm
            </a>
          </p>
        </div>
      </div>

      <div className="fixed bottom-6 right-6">
        <Button
          onClick={() => navigate("/report")}
          className="rounded-full w-14 h-14 shadow-lg"
        >
          <Plus className="h-6 w-6" />
        </Button>
      </div>
    </div>
  );
};

export default Index;
